import { faX } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Modal } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import './styles.scss';
import Input from '../../input/input';

export enum TypeOfView {
  NEW_TAB,
  RENAME_TAB,
  DUPLICATE_TAB,
  DELETE_TAB,
}

const TITLES = {
  [TypeOfView.NEW_TAB]: 'New tab',
  [TypeOfView.RENAME_TAB]: 'Rename tab',
  [TypeOfView.DUPLICATE_TAB]: 'Duplicate tab',
  [TypeOfView.DELETE_TAB]: 'Are you sure you want to delete this tab?',
};

interface IHandleCustomTabModal {
  open: boolean;
  handleClose: () => void;
  saveTab: (tab: string) => void;
  deleteTab: () => void;
  duplicateTab: (tab: string) => void;
  renameTab: (newName: string) => void;
  typeOfView: TypeOfView;
  verifyIfTabExists: (name: string) => boolean;
}

const HandleCustomTabModal = ({
  open,
  handleClose,
  saveTab,
  duplicateTab,
  renameTab,
  deleteTab,
  typeOfView,
  verifyIfTabExists,
}: IHandleCustomTabModal) => {
  const [tabName, setTabName] = useState<string | undefined>('');
  const [errors, setErrors] = useState<string | undefined>(undefined);

  const renderOptions = useMemo(() => {
    switch (typeOfView) {
      case TypeOfView.DELETE_TAB:
        return (
          <div className="items">
            <div className="message">All the data in this tab will be permanently deleted.</div>
          </div>
        );
      case TypeOfView.NEW_TAB:
      case TypeOfView.DUPLICATE_TAB:
      case TypeOfView.RENAME_TAB:
        return (
          <div className="items">
            <div className="item">
              <Input
                placeholder="Tab name"
                value={tabName}
                onChange={(e: any) => {
                  setTabName(e.target.value);
                }}
                errors={errors}
              />
            </div>
          </div>
        );
      default:
        return null;
    }
  }, [tabName, setTabName, typeOfView, errors]);

  const renderButtons = () => {
    if (typeOfView === TypeOfView.DELETE_TAB) {
      return (
        <div className="buttons">
          <Button className="cancel-button" onClick={handleClose}>
            Cancel
          </Button>
          <Button className="delete-button" onClick={handleSave}>
            Delete anyway
          </Button>
        </div>
      );
    }

    return (
      <div className="buttons">
        <Button className="add-button" onClick={handleSave} disabled={!tabName || !!errors}>
          Save
        </Button>
      </div>
    );
  };

  const handleSave = () => {
    handleClose();
    setTabName('');
    switch (typeOfView) {
      case TypeOfView.DELETE_TAB:
        deleteTab();
        break;
      case TypeOfView.DUPLICATE_TAB:
        duplicateTab(tabName!);
        break;
      case TypeOfView.RENAME_TAB:
        renameTab(tabName!);
        break;
      case TypeOfView.NEW_TAB:
        saveTab(tabName!);
        break;
    }
  };

  useEffect(() => {
    if (tabName && verifyIfTabExists(tabName)) {
      setErrors('Tab name already exists');
    } else {
      setErrors(undefined);
    }
  }, [tabName]);

  return (
    <Modal open={open} onClose={handleClose}>
      <div className="create-custom-tab-modal-container">
        <div className="create-custom-tab-modal-content">
          <div className="header">
            <h1>{TITLES[typeOfView]}</h1>
            <Button className="close-button" onClick={handleClose}>
              <FontAwesomeIcon icon={faX} />
            </Button>
          </div>
          {renderOptions}
          {renderButtons()}
        </div>
      </div>
    </Modal>
  );
};

export default HandleCustomTabModal;
